<template>
  <v-dialog
    v-model="dialogOpen"
    fullscreen
    transition="dialog-bottom-transition"
    persistent
    @keydown.esc="handleEscKey"
  >
    <v-card>
      <!-- Header toolbar -->
      <v-toolbar class="glass-topbar" flat height="64">
        <v-btn icon @click="close" class="topbar-icon-btn-danger">
          <v-icon>mdi-close</v-icon>
        </v-btn>
        <v-toolbar-title v-if="editMode" class="gradient-text ml-2">
          {{ $t('$vuetify.floorPlan.title') }}
        </v-toolbar-title>

        <v-spacer v-if="!editMode"></v-spacer>

        <!-- Room chips in user mode -->
        <div v-if="!editMode && rooms.length > 0" class="d-flex align-center justify-center">
          <v-chip
            v-for="(room, index) in rooms"
            :key="room.id"
            :color="activeRoomId === room.id ? '#3989C9' : 'rgba(57, 137, 201, 0.15)'"
            :text-color="activeRoomId === room.id ? 'white' : '#3989C9'"
            class="mx-1 room-chip"
            @click="onRoomChanged(room.id)"
          >
            {{ room.name }}
            <v-chip
              v-if="keyboardShortcutsEnabled && (showShortcutBadges || showBadgesTemporarily)"
              x-small
              color="white"
              :text-color="activeRoomId === room.id ? '#3989C9' : 'primary'"
              class="ml-1 shortcut-badge"
            >
              {{ getRoomShortcut(room.name, index) }}
            </v-chip>
          </v-chip>
        </div>

        <v-spacer></v-spacer>

        <!-- Zoom controls in user mode (in header) -->
        <div v-if="!editMode" class="d-flex align-center mr-2">
          <v-btn
            v-if="Math.round(zoom * 100) !== 100"
            icon
            @click="resetView"
            class="mr-2 zoom-control-btn-header"
          >
            <v-icon>mdi-restore</v-icon>
          </v-btn>
          <v-btn icon @click="zoomOut" class="zoom-control-btn-header">
            <v-icon>mdi-minus</v-icon>
          </v-btn>
          <span class="mx-2 zoom-label-header">{{ Math.round(zoom * 100) }}%</span>
          <v-btn icon @click="zoomIn" class="zoom-control-btn-header">
            <v-icon>mdi-plus</v-icon>
          </v-btn>
        </div>

        <!-- Edit/User mode toggle (only when not in readOnlyMode, user is manager, and not in transfer mode) -->
        <v-switch
          v-if="!readOnlyMode && isManager && !isTransferMode"
          v-model="editMode"
          :label="editMode ? $t('$vuetify.floorPlan.editMode') : $t('$vuetify.floorPlan.userMode')"
          class="mt-5 mr-4"
          color="#3989C9"
          hide-details
        ></v-switch>

        <!-- Save button (only in edit mode) -->
        <v-btn
          v-if="!readOnlyMode && editMode"
          @click="saveFloorPlan"
          class="soft-button-success mr-2"
          elevation="0"
        >
          <v-icon left small>mdi-content-save</v-icon>
          {{ $t('$vuetify.floorPlan.save') }}
        </v-btn>

        <!-- Close button on the right (in user mode) -->
        <v-btn v-if="!editMode" icon @click="close" class="topbar-icon-btn-danger">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <!-- Transfer mode indicator banner -->
      <v-alert
        v-if="isTransferMode"
        type="info"
        color="#3989C9"
        dark
        dense
        class="ma-0 transfer-mode-alert"
      >
        <v-row align="center" no-gutters>
          <v-col>
            <v-icon left small>mdi-transfer</v-icon>
            <strong>Način prijenosa:</strong> Odaberite stol za prebacivanje artikala
          </v-col>
          <v-col cols="auto" v-if="transferDestinationTable && transferDestinationTable.items && transferDestinationTable.items.length > 0">
            <v-chip small outlined dark>
              {{ transferDestinationTable.items.length }} artikl(a)
            </v-chip>
          </v-col>
        </v-row>
      </v-alert>

      <!-- Room tabs (only in edit mode) -->
      <room-tabs-bar
        v-if="rooms.length > 0 && editMode"
        :rooms="rooms"
        :active-room-id="activeRoomId"
        :edit-mode="!readOnlyMode && editMode"
        :zoom="zoom"
        :keyboard-shortcuts-enabled="keyboardShortcutsEnabled"
        :show-shortcut-badges="showShortcutBadges"
        :show-badges-temporarily="showBadgesTemporarily"
        @room-changed="onRoomChanged"
        @room-added="onRoomAdded"
        @room-updated="onRoomUpdated"
        @room-deleted="onRoomDeleted"
        @rooms-reordered="onRoomsReordered"
        @zoom-in="zoomIn"
        @zoom-out="zoomOut"
        @reset-view="resetView"
        @close-floor-plan="close"
      ></room-tabs-bar>

      <!-- Main content area -->
      <v-card-text class="pa-0" :style="`height: calc(100vh - ${editMode ? '128px' : '64px'}); position: relative;`">
        <!-- Toolbar (only in edit mode and not readOnlyMode) -->
        <floor-plan-toolbar
          v-if="!readOnlyMode && editMode"
          :active-tool="activeTool"
          :zoom="zoom"
          :snap-to-grid="snapToGrid"
          :show-grid="showGrid"
          :background-pattern="activeRoom ? activeRoom.backgroundPattern : 'none'"
          :background-color="activeRoom ? activeRoom.backgroundColor : '#f5f5f5'"
          @tool-changed="onToolChanged"
          @zoom-in="zoomIn"
          @zoom-out="zoomOut"
          @zoom-changed="zoom = $event"
          @reset-view="resetView"
          @toggle-grid="showGrid = !showGrid"
          @toggle-snap="snapToGrid = !snapToGrid"
          @pattern-changed="onBackgroundPatternChanged"
          @background-color-changed="onBackgroundColorChanged"
        ></floor-plan-toolbar>

        <!-- Canvas -->
        <div
          v-if="activeRoom"
          :style="{
            marginLeft: (!readOnlyMode && editMode) ? '220px' : '0',
            height: '100%'
          }"
        >
          <floor-plan-canvas
            ref="canvas"
            :room="activeRoom"
            :edit-mode="!readOnlyMode && editMode"
            :active-tool="activeTool"
            :tables="availableTables"
            :zoom="zoom"
            :snap-to-grid="snapToGrid"
            :show-grid="showGrid"
            :background-pattern="activeRoom.backgroundPattern || 'none'"
            :background-color="activeRoom.backgroundColor || '#f5f5f5'"
            :keyboard-shortcuts-enabled="keyboardShortcutsEnabled"
            :show-shortcut-badges="showShortcutBadges"
            :show-badges-temporarily="showBadgesTemporarily"
            @table-clicked="onTableClicked"
            @object-added="onObjectAdded"
            @object-updated="onObjectUpdated"
            @object-deleted="onObjectDeleted"
            @object-selected="onObjectSelected"
            @tool-changed="onToolChanged"
            @zoom-changed="zoom = $event"
          ></floor-plan-canvas>
        </div>

        <!-- Empty state -->
        <v-container v-else class="fill-height">
          <v-row align="center" justify="center">
            <v-col class="text-center">
              <v-icon size="100" color="grey lighten-2">mdi-floor-plan</v-icon>
              <h2 class="grey--text mt-4">{{ $t('$vuetify.floorPlan.noRooms') }}</h2>
              <v-btn
                v-if="!readOnlyMode && isManager"
                class="soft-button mt-4"
                elevation="0"
                @click="createFirstRoom"
              >
                <v-icon left small>mdi-plus</v-icon>
                {{ $t('$vuetify.floorPlan.createFirstRoom') }}
              </v-btn>
            </v-col>
          </v-row>
        </v-container>

        <!-- Properties Panel (only in edit mode, not readOnlyMode, and when object is selected) -->
        <floor-plan-properties-panel
          v-if="!readOnlyMode && editMode && activeRoom && selectedObject"
          :selected-object="selectedObject"
          @property-changed="onPropertyChanged"
          @link-table="openTableLinkingDialog"
          @duplicate-object="onDuplicateObject"
          @delete-object="onDeleteObject"
        ></floor-plan-properties-panel>
      </v-card-text>

      <!-- Table Linking Dialog -->
      <table-linking-dialog
        v-if="!readOnlyMode"
        ref="tableLinkingDialog"
        :table-object="selectedObject"
        :available-tables="availableTables"
        :linked-table-ids="linkedTableIds"
        @link-table="onLinkTable"
        @unlink-table="onUnlinkTable"
        @add-table="onAddNewTable"
      ></table-linking-dialog>

      <!-- Loading Overlay -->
      <v-overlay :value="isSaving" z-index="9999" opacity="0.8">
        <div class="text-center">
          <v-progress-circular
            indeterminate
            size="64"
            width="4"
            color="#00B3BE"
          ></v-progress-circular>
          <p class="mt-4 white--text text-h6 font-weight-medium">
            {{ savingMessage }}
          </p>
        </div>
      </v-overlay>
    </v-card>
  </v-dialog>
</template>

<script>
import RoomTabsBar from '@/modules/cash-register/components/floor-plan/RoomTabsBar.vue'
import FloorPlanToolbar from '@/modules/cash-register/components/floor-plan/FloorPlanToolbar.vue'
import FloorPlanCanvas from '@/modules/cash-register/components/floor-plan/FloorPlanCanvas.vue'
import FloorPlanPropertiesPanel from '@/modules/cash-register/components/floor-plan/FloorPlanPropertiesPanel.vue'
import TableLinkingDialog from '@/modules/cash-register/components/floor-plan/TableLinkingDialog.vue'
import EventBus from '@/plugins/event-bus'
import state from '@/state'
import { df } from '@/plugins/firebase'
import { v4 as uuidv4 } from 'uuid'

export default {
  name: 'FloorPlanDialog',
  components: {
    RoomTabsBar,
    FloorPlanToolbar,
    FloorPlanCanvas,
    FloorPlanPropertiesPanel,
    TableLinkingDialog
  },
  inject: ['showMsgBox', 'confirm', 'confirmClose', 'showLoader', 'hideLoader'],
  props: {
    availableTables: {
      type: Array,
      default: () => []
    },
    readOnlyMode: {
      type: Boolean,
      default: false
    },
    transferMode: {
      type: Boolean,
      default: false
    },
    sourceTable: {
      type: Object,
      default: null
    },
    destinationTable: {
      type: Object,
      default: null
    }
  },
  data () {
    return {
      dialogOpen: false,
      editMode: false,
      rooms: [],
      activeRoomId: null,
      activeTool: 'select',
      zoom: 1,
      snapToGrid: true,
      showGrid: true,
      isManager: false,
      listeners: [],
      selectedObject: null,
      keyboardShortcutsEnabled: false,
      showShortcutBadges: false,
      showBadgesTemporarily: false,
      spaceKeyPressed: false,
      spaceKeyPressTimer: null,
      isSaving: false,
      savingMessage: '',
      isProcessingTransfer: false, // Za prikaz loading overlay-a tijekom transfera
      isTransferMode: false,
      transferSourceTable: null,
      transferDestinationTable: null,
      mouseX: 0 // Track mouse X position
    }
  },
  computed: {
    activeRoom () {
      return this.rooms.find(room => room.id === this.activeRoomId)
    },
    linkedTableIds () {
      // Collect linked table IDs from ALL rooms, not just active room
      const linkedIds = []
      this.rooms.forEach(room => {
        if (room.objects) {
          room.objects.forEach(obj => {
            if ((obj.type === 'table' || obj.type === 'bar') && obj.tableId) {
              linkedIds.push(obj.tableId)
            }
          })
        }
      })
      return linkedIds
    }
  },
  methods: {
    /**
     * Normalize Croatian characters to English
     */
    normalizeCharacters (text) {
      if (!text) return text
      const charMap = {
        š: 's',
        Š: 'S',
        č: 'c',
        Č: 'C',
        ć: 'c',
        Ć: 'C',
        ž: 'z',
        Ž: 'Z',
        đ: 'd',
        Đ: 'D'
      }
      return text.replace(/[šŠčČćĆžŽđĐ]/g, char => charMap[char] || char)
    },

    /**
     * Get room shortcut (first letter of room name)
     */
    getRoomShortcut (roomName, index) {
      const normalized = this.normalizeCharacters(roomName || '')
      let letter = normalized[0] ? normalized[0].toUpperCase() : String.fromCharCode(65 + index)

      // Check for duplicates
      const roomShortcuts = {}
      this.rooms.forEach((room, idx) => {
        if (idx <= index) {
          const norm = this.normalizeCharacters(room.name || '')
          const l = norm[0] ? norm[0].toUpperCase() : String.fromCharCode(65 + idx)
          roomShortcuts[l] = (roomShortcuts[l] || 0) + 1

          if (idx === index && roomShortcuts[l] > 1) {
            letter = l + (roomShortcuts[l] - 1)
          }
        }
      })

      return letter
    },

    handleRoomKeyPress (e) {
      // Ignore if keyboard shortcuts are disabled
      if (!this.keyboardShortcutsEnabled) return

      // Ignore if in edit mode
      if (this.editMode) return

      // Ignore if user is typing in an input field
      if (['INPUT', 'TEXTAREA'].includes(e.target.tagName)) return

      const key = e.key

      // Handle letters for room selection
      if (/^[a-zA-Z]$/.test(key)) {
        const letter = key.toUpperCase()

        // Find room with matching shortcut
        const roomIndex = this.rooms.findIndex((room, idx) => {
          return this.getRoomShortcut(room.name, idx) === letter
        })

        if (roomIndex >= 0) {
          // Prevent event from bubbling to other components
          e.preventDefault()
          e.stopPropagation()

          const room = this.rooms[roomIndex]
          this.onRoomChanged(room.id)
        }
      }
    },

    getLocalStorageKey () {
      return `keyboard_shortcuts_${state.getCashRegister().id}`
    },
    loadKeyboardSettings () {
      try {
        const key = this.getLocalStorageKey()
        const saved = localStorage.getItem(key)
        if (saved) {
          const settings = JSON.parse(saved)
          this.keyboardShortcutsEnabled = settings.enabled !== undefined ? settings.enabled : false
          this.showShortcutBadges = settings.showBadges !== undefined ? settings.showBadges : false
        }
      } catch (err) {
        console.error('Error loading keyboard settings:', err)
      }
    },
    handleSpaceKeyDown (e) {
      // Only process if it's the Space key and shortcuts are enabled
      if (e.key !== ' ') return
      if (!this.keyboardShortcutsEnabled) return

      // Ignore if user is typing in an input field
      if (['INPUT', 'TEXTAREA'].includes(e.target.tagName)) return

      // Ignore if in edit mode
      if (this.editMode) return

      // If Space key is already pressed, don't start a new timer
      if (this.spaceKeyPressed) return

      // Prevent default space behavior (scrolling)
      e.preventDefault()

      // Mark that Space key is pressed
      this.spaceKeyPressed = true

      // Start timer for 600ms
      this.spaceKeyPressTimer = setTimeout(() => {
        // After 600ms, show badges temporarily
        this.showBadgesTemporarily = true
      }, 600)
    },
    handleSpaceKeyUp (e) {
      // Only process if it's the Space key
      if (e.key !== ' ') return

      // Clear the timer if key is released before 600ms
      if (this.spaceKeyPressTimer) {
        clearTimeout(this.spaceKeyPressTimer)
        this.spaceKeyPressTimer = null
      }

      // Hide badges and reset flag
      this.showBadgesTemporarily = false
      this.spaceKeyPressed = false
    },
    open (transferContext = null) {
      this.dialogOpen = true

      // Handle transfer mode
      if (transferContext) {
        this.isTransferMode = true
        this.transferSourceTable = transferContext.sourceTable
        this.transferDestinationTable = transferContext.destinationTable
        this.editMode = false // Force user mode for transfers
      } else {
        this.isTransferMode = false
        this.transferSourceTable = null
        this.transferDestinationTable = null
        // Set editMode to true by default when not in readOnlyMode
        if (!this.readOnlyMode && this.isManager) {
          this.editMode = true
        } else {
          this.editMode = false
        }
      }

      this.loadKeyboardSettings()
      this.loadFloorPlan()
    },
    trackMousePosition (event) {
      this.mouseX = event.clientX
    },
    handleEscKey () {
      // If in edit mode, don't close dialog - ESC is used for canceling tools
      if (this.editMode) {
        return
      }
      // If not in edit mode, close the dialog
      this.close()
    },
    close () {
      // Spriječi zatvaranje ako je transfer u tijeku
      if (this.isProcessingTransfer) {
        console.warn('⚠️ Ne mogu zatvoriti FloorPlan dok je transfer u tijeku')
        return
      }

      if (this.editMode && this.hasUnsavedChanges()) {
        this.confirm({
          title: this.$t('$vuetify.floorPlan.unsavedChanges'),
          message: this.$t('$vuetify.floorPlan.unsavedChangesMessage'),
          options: {
            toolbar: true,
            width: 410,
            confirmText: this.$t('$vuetify.yes'),
            cancelText: this.$t('$vuetify.no')
          }
        }).then((resp) => {
          if (resp) {
            this.dialogOpen = false
            // Ako je kursor nad side nav-om, emitiraj event za blokiranje hover-a
            if (this.mouseX < 60) {
              EventBus.$emit('block-sidenav-hover-temporarily')
            }
          }
          this.confirmClose()
        })
      } else {
        this.dialogOpen = false
        // Ako je kursor nad side nav-om, emitiraj event za blokiranje hover-a
        if (this.mouseX < 60) {
          EventBus.$emit('block-sidenav-hover-temporarily')
        }
      }
    },
    async loadFloorPlan () {
      try {
        this.showLoader(this.$t('$vuetify.floorPlan.loading'))

        const locationId = state.getPointOfSale().id
        const roomsSnapshot = await df
          .collection(`locations/${locationId}/rooms`)
          .orderBy('order')
          .get()

        this.rooms = []
        roomsSnapshot.docs.forEach(doc => {
          this.rooms.push({
            id: doc.id,
            ...doc.data()
          })
        })

        // Set active room to first room or saved preference
        if (this.rooms.length > 0) {
          const savedRoomId = localStorage.getItem('floorPlan_activeRoomId')
          const savedRoom = this.rooms.find(r => r.id === savedRoomId)
          this.activeRoomId = savedRoom ? savedRoom.id : this.rooms[0].id
        }

        this.hideLoader()
      } catch (err) {
        console.error('Error loading floor plan:', err)
        this.hideLoader()
        this.showMsgBox({
          text: this.$t('$vuetify.floorPlan.loadError'),
          actions: ['cancel'],
          cancelBtnText: 'OK',
          color: 'error'
        })
      }
    },
    async saveFloorPlan () {
      try {
        // Show overlay
        this.isSaving = true
        this.savingMessage = this.$t('$vuetify.floorPlan.saving')
        this.showLoader(this.$t('$vuetify.floorPlan.saving'))

        const locationId = state.getPointOfSale().id
        const batch = df.batch()

        // Save all rooms
        this.rooms.forEach(room => {
          const roomRef = df.doc(`locations/${locationId}/rooms/${room.id}`)
          // Serialize objects to plain JavaScript objects for Firestore
          const serializedObjects = (room.objects || []).map(obj => {
            return obj.toJSON ? obj.toJSON() : obj
          })
          batch.set(roomRef, {
            name: room.name,
            order: room.order,
            active: room.active,
            width: room.width,
            height: room.height,
            objects: serializedObjects,
            backgroundPattern: room.backgroundPattern || 'none',
            backgroundColor: room.backgroundColor || '#f5f5f5'
          })
        })

        await batch.commit()

        // Hide overlay
        this.isSaving = false
        this.hideLoader()
        this.showMsgBox({
          text: this.$t('$vuetify.floorPlan.saved'),
          actions: ['cancel'],
          cancelBtnText: 'OK',
          color: 'success'
        })
      } catch (err) {
        console.error('Error saving floor plan:', err)
        // Hide overlay on error
        this.isSaving = false
        this.hideLoader()
        this.showMsgBox({
          text: this.$t('$vuetify.floorPlan.saveError'),
          actions: ['cancel'],
          cancelBtnText: 'OK',
          color: 'error'
        })
      }
    },
    onRoomChanged (roomId) {
      this.activeRoomId = roomId
      // Save preference
      localStorage.setItem('floorPlan_activeRoomId', roomId)
    },
    onRoomsReordered (reorderedRooms) {
      const newOrder = reorderedRooms.map(r => r.id)
      const reordered = newOrder
        .map(id => this.rooms.find(r => r.id === id))
        .filter(Boolean)
        .map((room, index) => ({ ...room, order: index + 1 }))
      this.rooms.splice(0, this.rooms.length, ...reordered)
    },
    onRoomAdded (room) {
      this.rooms.push(room)
      this.activeRoomId = room.id
    },
    onRoomUpdated (updatedRoom) {
      const index = this.rooms.findIndex(r => r.id === updatedRoom.id)
      if (index !== -1) {
        const existingRoom = this.rooms[index]
        // Preserve objects array when updating room properties
        const mergedRoom = {
          ...existingRoom,
          ...updatedRoom,
          objects: existingRoom.objects || []
        }
        this.$set(this.rooms, index, mergedRoom)
      }
    },
    async onRoomDeleted (roomId) {
      const index = this.rooms.findIndex(r => r.id === roomId)
      if (index !== -1) {
        try {
          this.showLoader('Brisanje prostorije...')

          // Delete from Firestore
          const locationId = state.getPointOfSale().id
          await df.doc(`locations/${locationId}/rooms/${roomId}`).delete()

          // Remove from local array
          this.rooms.splice(index, 1)

          // Switch to another room if deleted room was active
          if (this.activeRoomId === roomId && this.rooms.length > 0) {
            this.activeRoomId = this.rooms[0].id
          }

          this.hideLoader()
          this.showMsgBox({
            text: 'Prostorija je uspješno obrisana',
            actions: ['cancel'],
            cancelBtnText: 'OK',
            color: 'success'
          })
        } catch (err) {
          console.error('Error deleting room:', err)
          this.hideLoader()
          this.showMsgBox({
            text: 'Greška pri brisanju prostorije',
            actions: ['cancel'],
            cancelBtnText: 'OK',
            color: 'error'
          })
        }
      }
    },
    createFirstRoom () {
      const room = {
        id: uuidv4(),
        name: this.$t('$vuetify.floorPlan.room') + ' 1',
        order: 1,
        active: true,
        width: 1200,
        height: 800,
        objects: [],
        backgroundPattern: 'none',
        backgroundColor: '#f5f5f5'
      }
      this.onRoomAdded(room)
      this.editMode = true
    },
    async onTableClicked (tableId) {
      console.log('FloorPlan: onTableClicked', tableId, 'isTransferMode:', this.isTransferMode)

      if (this.isTransferMode) {
        // ✅ Early return ako je transfer već u tijeku
        if (this.isProcessingTransfer) {
          console.warn('⚠️ Transfer već u tijeku, ignoriram dupli poziv')
          return
        }

        // Provjera - ne dozvoli odabir istog stola
        if (this.transferSourceTable && tableId === this.transferSourceTable.id) {
          this.showMsgBox({
            text: 'Ne možete prebaciti artikle na isti stol.',
            actions: ['cancel'],
            cancelBtnText: 'OK',
            color: 'warning'
          })
          return
        }

        // Postavi loading state
        console.log('FloorPlan: Postavljam isProcessingTransfer = true')
        this.isProcessingTransfer = true

        // Čekaj da Vue renderira overlay i dodaj mali delay da korisnik sigurno vidi overlay
        await this.$nextTick()
        await new Promise(resolve => setTimeout(resolve, 50))
        console.log('FloorPlan: Overlay bi trebao biti vidljiv sada')

        console.log('FloorPlan: Emitiram select-table-for-transfer event')
        EventBus.$emit('select-table-for-transfer', {
          selectedTableId: tableId
        })
        console.log('FloorPlan: NE zatvaram dialog - čekam table-transfer-completed event')
        // NE zatvaraj odmah - zatvorit će se preko EventBus eventa
      } else {
        // Normal mode: emit existing event
        EventBus.$emit('select-table-from-floor-plan', tableId)
        this.close()
      }
    },
    onObjectAdded (object) {
      if (this.activeRoom) {
        this.activeRoom.objects.push(object)
      }
    },
    onObjectUpdated (object) {
      if (this.activeRoom) {
        const index = this.activeRoom.objects.findIndex(o => o.id === object.id)
        if (index !== -1) {
          this.$set(this.activeRoom.objects, index, object)
        }
      }
    },
    onObjectDeleted (objectId) {
      if (this.activeRoom) {
        const index = this.activeRoom.objects.findIndex(o => o.id === objectId)
        if (index !== -1) {
          this.activeRoom.objects.splice(index, 1)
        }
      }
    },
    onToolChanged (tool) {
      this.activeTool = tool
    },
    onBackgroundPatternChanged (pattern) {
      if (this.activeRoom) {
        this.$set(this.activeRoom, 'backgroundPattern', pattern)
      }
    },
    onBackgroundColorChanged (color) {
      if (this.activeRoom) {
        this.$set(this.activeRoom, 'backgroundColor', color)
      }
    },
    zoomIn () {
      this.zoom = Math.min(this.zoom + 0.1, 3)
    },
    zoomOut () {
      this.zoom = Math.max(this.zoom - 0.1, 0.3)
    },
    resetView () {
      this.zoom = 1
      if (this.$refs.canvas) {
        this.$refs.canvas.resetPosition()
      }
    },
    hasUnsavedChanges () {
      // TODO: Implement change tracking
      return false
    },
    onObjectSelected (object) {
      this.selectedObject = object
    },
    onPropertyChanged ({ key, value }) {
      if (this.selectedObject) {
        this.selectedObject[key] = value
        this.onObjectUpdated(this.selectedObject)

        // If tableName is changed and table is linked, update in Firestore
        if (key === 'tableName' && this.selectedObject.type === 'table' && this.selectedObject.tableId) {
          this.updateTableNameInFirestore(this.selectedObject.tableId, value)
        }

        // Update canvas
        if (this.$refs.canvas) {
          this.$refs.canvas.updateObjectVisual(this.selectedObject)
        }
      }
    },
    updateTableNameInFirestore (tableId, newName) {
      const pointOfSaleId = state.getPointOfSale().id
      df.doc(`location_orders/${pointOfSaleId}/tables/${tableId}`).update({
        name: newName
      })
        .then(() => {
          console.info('Table name successfully updated in Firestore.')
        })
        .catch((err) => {
          console.error('Error updating table name in Firestore:', err)
          this.showMsgBox({
            text: 'Greška pri ažuriranju naziva stola',
            actions: ['cancel'],
            cancelBtnText: 'OK',
            color: 'error'
          })
        })
    },
    openTableLinkingDialog () {
      if (this.$refs.tableLinkingDialog) {
        this.$refs.tableLinkingDialog.open()
      }
    },
    onLinkTable ({ canvasObjectId, tableId }) {
      this.showLoader('Povezivanje stola...')

      const object = this.activeRoom.objects.find(o => o.id === canvasObjectId)
      if (object) {
        object.tableId = tableId
        this.onObjectUpdated(object)

        // Update selectedObject if it's the same object
        if (this.selectedObject && this.selectedObject.id === canvasObjectId) {
          this.selectedObject.tableId = tableId
        }

        // Update canvas
        if (this.$refs.canvas) {
          this.$refs.canvas.updateObjectVisual(object)
        }

        this.hideLoader()
        this.showMsgBox({
          text: 'Stol je uspješno povezan',
          actions: ['cancel'],
          cancelBtnText: 'OK',
          color: 'success'
        })
      } else {
        this.hideLoader()
      }
    },
    onUnlinkTable (canvasObjectId) {
      const object = this.activeRoom.objects.find(o => o.id === canvasObjectId)
      if (object) {
        object.tableId = null
        this.onObjectUpdated(object)

        // Update selectedObject if it's the same object
        if (this.selectedObject && this.selectedObject.id === canvasObjectId) {
          this.selectedObject.tableId = null
        }

        // Update canvas
        if (this.$refs.canvas) {
          this.$refs.canvas.updateObjectVisual(object)
        }
      }
    },
    onDuplicateObject () {
      if (this.$refs.canvas) {
        this.$refs.canvas.duplicateSelectedObject()
      }
    },
    onDeleteObject (object) {
      this.confirm({
        title: this.$t('$vuetify.floorPlan.deleteObject'),
        message: this.$t('$vuetify.floorPlan.deleteObjectMessage'),
        options: {
          toolbar: true,
          width: 410,
          confirmText: this.$t('$vuetify.yes'),
          cancelText: this.$t('$vuetify.no')
        }
      }).then((resp) => {
        if (resp) {
          this.onObjectDeleted(object.id)
          this.selectedObject = null
        }
        this.confirmClose()
      })
    },
    async onAddNewTable ({ table, persist, canvasObjectId }) {
      try {
        this.showLoader(this.$t('$vuetify.tables.create'))

        // Generate table ID
        const tableId = uuidv4()
        table.id = tableId
        table.persistent = persist

        // Save table to Firestore
        const pointOfSaleId = state.getPointOfSale().id
        const docRef = df.doc(`location_orders/${pointOfSaleId}/tables/${tableId}`)

        await docRef.set({
          id: tableId,
          name: table.name,
          number: table.number,
          items: table.items || [],
          persistent: persist,
          color: table.color || 'primary',
          locked_by: null,
          locked: false,
          created_at: new Date()
        })

        // Add to available tables list
        this.availableTables.push(table)

        // Link the table to the canvas object
        const object = this.activeRoom.objects.find(o => o.id === canvasObjectId)
        if (object) {
          object.tableId = tableId
          this.onObjectUpdated(object)

          // Update selectedObject if it's the same object
          if (this.selectedObject && this.selectedObject.id === canvasObjectId) {
            this.selectedObject.tableId = tableId
          }

          // Update canvas
          if (this.$refs.canvas) {
            this.$refs.canvas.updateObjectVisual(object)
          }
        }

        this.hideLoader()
        this.showMsgBox({
          text: `Stol "${table.name}" je uspješno kreiran i povezan`,
          actions: ['cancel'],
          cancelBtnText: 'OK',
          color: 'success'
        })
      } catch (err) {
        console.error('Error adding new table:', err)
        this.hideLoader()
        this.showMsgBox({
          text: 'Greška pri dodavanju stola',
          actions: ['cancel'],
          cancelBtnText: 'OK',
          color: 'error'
        })
      }
    }
  },
  created () {
    const user = state.getCashRegisterUser().id ? state.getCashRegisterUser() : state.getUser()
    this.isManager = !!(user.roles && (
      (user.roles.VODITELJ && user.roles.VODITELJ === true) ||
      (user.roles.ADMIN && user.roles.ADMIN === true) ||
      (user.roles.SUPERADMIN && user.roles.SUPERADMIN === true)
    ))

    // Listen for floor plan dialog open event (normal mode)
    EventBus.$on('open-floor-plan-dialog', () => {
      this.open()
    })

    // Listen for floor plan dialog open in transfer mode
    EventBus.$on('open-floor-plan-for-transfer', (transferContext) => {
      this.open(transferContext)
    })

    // Listen for transfer completion
    EventBus.$on('table-transfer-completed', () => {
      console.log('FloorPlan: primio table-transfer-completed event')
      // Resetiraj flag i zatvori dialog direktno
      this.isProcessingTransfer = false
      this.dialogOpen = false
    })

    // Listen for keyboard shortcuts changed event
    EventBus.$off('keyboard-shortcuts-changed')
    EventBus.$on('keyboard-shortcuts-changed', (settings) => {
      this.keyboardShortcutsEnabled = settings.enabled
      this.showShortcutBadges = settings.showBadges
    })
  },
  mounted () {
    // Register keyboard listeners for Space key and room shortcuts
    document.addEventListener('keydown', this.handleSpaceKeyDown)
    document.addEventListener('keyup', this.handleSpaceKeyUp)
    document.addEventListener('keydown', this.handleRoomKeyPress)
    // Track mouse position to detect if cursor is over side nav
    document.addEventListener('mousemove', this.trackMousePosition)
  },
  beforeDestroy () {
    // Clean up keyboard listeners
    document.removeEventListener('keydown', this.handleSpaceKeyDown)
    document.removeEventListener('keyup', this.handleSpaceKeyUp)
    document.removeEventListener('keydown', this.handleRoomKeyPress)
    // Clean up mouse position tracker
    document.removeEventListener('mousemove', this.trackMousePosition)
    if (this.spaceKeyPressTimer) {
      clearTimeout(this.spaceKeyPressTimer)
    }

    // Clean up listeners
    this.listeners.forEach(listener => {
      if (typeof listener === 'function') {
        listener()
      }
    })
    EventBus.$off('open-floor-plan-dialog')
    EventBus.$off('open-floor-plan-for-transfer')
    EventBus.$off('table-transfer-completed')
    EventBus.$off('keyboard-shortcuts-changed')
  }
}
</script>

<style scoped>
.v-card-text {
  overflow: hidden;
}

/* Glass TopBar Style */
.glass-topbar {
  background: rgba(255, 255, 255, 0.85) !important;
  backdrop-filter: blur(20px) !important;
  -webkit-backdrop-filter: blur(20px) !important;
  border-bottom: 1px solid rgba(0, 179, 190, 0.2) !important;
  box-shadow: 0 4px 24px rgba(0, 179, 190, 0.08) !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

/* Room Chips in Header */
.room-chip {
  cursor: pointer !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
  font-weight: 600 !important;
  font-size: 15px !important;
  letter-spacing: 0.3px !important;
  border-radius: 10px !important;
  box-shadow: 0 2px 6px rgba(57, 137, 201, 0.15) !important;
  height: 40px !important;
  padding: 0 16px !important;
}

.room-chip:hover {
  transform: translateY(-2px) !important;
  box-shadow: 0 4px 12px rgba(57, 137, 201, 0.25) !important;
}

.shortcut-badge {
  font-weight: 700 !important;
  font-size: 11px !important;
  min-width: 22px !important;
  height: 22px !important;
  border-radius: 5px !important;
}

/* Zoom Controls in Header */
.zoom-control-btn-header {
  background: rgba(255, 255, 255, 0.8) !important;
  backdrop-filter: blur(10px) !important;
  border: 1px solid rgba(57, 137, 201, 0.2) !important;
  color: #3989C9 !important;
  border-radius: 10px !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
  box-shadow: 0 2px 4px rgba(57, 137, 201, 0.1) !important;
  width: 40px !important;
  height: 40px !important;
}

.zoom-control-btn-header:hover {
  background: rgba(57, 137, 201, 0.12) !important;
  border-color: rgba(57, 137, 201, 0.3) !important;
  transform: scale(1.08) !important;
  box-shadow: 0 4px 8px rgba(57, 137, 201, 0.2) !important;
}

.zoom-control-btn-header .v-icon {
  font-size: 22px !important;
}

.zoom-label-header {
  font-weight: 700 !important;
  color: rgba(57, 137, 201, 0.95) !important;
  min-width: 60px;
  text-align: center;
  font-size: 15px !important;
  letter-spacing: 0.3px !important;
  padding: 8px 12px !important;
  background: rgba(57, 137, 201, 0.08) !important;
  border-radius: 8px !important;
  height: 40px !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
}

/* Gradient Text Style for Title */
.gradient-text {
  font-size: 24px !important;
  font-weight: 600 !important;
  background: linear-gradient(135deg,
    rgba(0, 179, 190, 1) 0%,
    rgba(57, 137, 201, 1) 50%,
    rgba(97, 84, 164, 1) 100%) !important;
  -webkit-background-clip: text !important;
  -webkit-text-fill-color: transparent !important;
  background-clip: text !important;
  letter-spacing: 0.5px !important;
  display: inline-block !important;
}

/* TopBar Icon Button */
.topbar-icon-btn {
  color: #3989C9 !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
  width: 40px !important;
  height: 40px !important;
}

.topbar-icon-btn:hover {
  background: rgba(57, 137, 201, 0.1) !important;
  transform: scale(1.05);
}

.topbar-icon-btn .v-icon {
  font-size: 24px !important;
}

.topbar-icon-btn-danger {
  color: #F44336 !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
  width: 40px !important;
  height: 40px !important;
}

.topbar-icon-btn-danger:hover {
  background: rgba(244, 67, 54, 0.1) !important;
  transform: scale(1.05);
}

.topbar-icon-btn-danger .v-icon {
  font-size: 24px !important;
}

/* Soft Button Style (Primary) */
.soft-button {
  background: rgba(255, 255, 255, 0.8) !important;
  color: #3989C9 !important;
  border-radius: 12px !important;
  border: 1px solid rgba(57, 137, 201, 0.15) !important;
  box-shadow: 0 2px 8px rgba(57, 137, 201, 0.15) !important;
  backdrop-filter: blur(10px) !important;
  text-transform: none !important;
  font-weight: 600 !important;
  font-size: 14px !important;
  letter-spacing: 0.3px !important;
  padding: 10px 24px !important;
  height: 40px !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.soft-button:hover {
  box-shadow: 0 4px 12px rgba(57, 137, 201, 0.25) !important;
  border-color: rgba(57, 137, 201, 0.3) !important;
  transform: translateY(-1px);
}

.soft-button .v-icon {
  font-size: 20px !important;
}

/* Soft Button Success Style */
.soft-button-success {
  background: rgba(76, 175, 80, 0.15) !important;
  color: #4CAF50 !important;
  border-radius: 12px !important;
  border: 1px solid rgba(76, 175, 80, 0.3) !important;
  box-shadow: 0 2px 8px rgba(76, 175, 80, 0.15) !important;
  backdrop-filter: blur(10px) !important;
  text-transform: none !important;
  font-weight: 600 !important;
  font-size: 14px !important;
  letter-spacing: 0.3px !important;
  padding: 10px 24px !important;
  height: 40px !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.soft-button-success:hover {
  background: rgba(76, 175, 80, 0.25) !important;
  box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3) !important;
  border-color: rgba(76, 175, 80, 0.5) !important;
  transform: translateY(-1px);
}

.soft-button-success .v-icon {
  font-size: 20px !important;
}

/* Transfer Mode Alert Banner */
.transfer-mode-alert {
  border-radius: 0 !important;
  border-left: 4px solid #FFC107 !important;
}
</style>
